import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface ProductVersion {
  version: string;
  date: string;
  notes: string;
}

export interface ProductEdition {
  key: string;
  label: string;
  price: number;
}

export interface AdminProduct {
  id: number;
  name: string;
  slug: string;
  category: string | null;
  summary: string | null;
  description: string | null;
  icon: string;
  latest_version: string | null;
  versions: ProductVersion[] | null;
  editions: ProductEdition[] | null;
  rating: number;
  downloads: number;
  status: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProductCategory {
  id: number;
  name: string;
  sort_order: number;
}

export interface ProductCategoryDraft {
  id?: number;
  name: string;
  sort_order: number;
}

export interface ProductDraft {
  id?: number;
  name: string;
  slug: string;
  category: string;
  icon: string;
  summary: string;
  description: string;
  latest_version: string;
  versions: ProductVersion[];
  editions: ProductEdition[];
  rating: number;
  downloads: number;
  status: string;
  sort_order: number;
}

async function invokeProducts<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-products", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseAdminProductsResult {
  products: AdminProduct[];
  categories: ProductCategory[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  saveProduct: (draft: ProductDraft) => Promise<void>;
  setStatus: (id: number, status: string) => Promise<void>;
  batchSetStatus: (ids: number[], status: string) => Promise<number>;
  removeProduct: (id: number) => Promise<void>;
  saveCategory: (draft: ProductCategoryDraft) => Promise<void>;
  removeCategory: (id: number) => Promise<void>;
}

export function useAdminProducts(): UseAdminProductsResult {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, categoryData] = await Promise.all([
        invokeProducts<{ products: AdminProduct[] }>({ action: "list" }),
        invokeProducts<{ categories: ProductCategory[] }>({ action: "listCategories" }),
      ]);
      setProducts(data.products ?? []);
      setCategories(categoryData.categories ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "软件产品加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveProduct = useCallback(
    async (draft: ProductDraft) => {
      await invokeProducts({ action: "save", product: draft });
      await load();
    },
    [load],
  );

  const setStatus = useCallback(
    async (id: number, status: string) => {
      await invokeProducts({ action: "setStatus", id, status });
      await load();
    },
    [load],
  );

  const batchSetStatus = useCallback(
    async (ids: number[], status: string) => {
      const result = await invokeProducts<{ updated: number }>({ action: "setStatusMany", ids, status });
      await load();
      return result.updated ?? 0;
    },
    [load],
  );

  const removeProduct = useCallback(
    async (id: number) => {
      await invokeProducts({ action: "delete", id });
      await load();
    },
    [load],
  );

  const saveCategory = useCallback(
    async (draft: ProductCategoryDraft) => {
      await invokeProducts({ action: "saveCategory", category: draft });
      await load();
    },
    [load],
  );

  const removeCategory = useCallback(
    async (id: number) => {
      await invokeProducts({ action: "deleteCategory", id });
      await load();
    },
    [load],
  );

  return {
    products,
    categories,
    loading,
    error,
    reload: load,
    saveProduct,
    setStatus,
    batchSetStatus,
    removeProduct,
    saveCategory,
    removeCategory,
  };
}